import { bbox, featureCollection } from "@turf/turf";
import { useMemo } from "react";

import { MapBounds } from "~/modules/map/components/MapButtonFitBounds/types";
import {
    MAP_PADDING_PANEL_HORIZONTAL,
    MAP_PANEL_WIDTH_THIN,
    MAP_PANEL_WIDTH_WIDE,
    NETHERLANDS_BOUNDS,
} from "~/modules/map/mapConstants";

import { MapEditorLayerFragment } from "../api/MapEditorLayer.fragment.generated";

type UseMapEditorSelectionParams = {
    allLayerData?: MapEditorLayerFragment[];
    selectedLayerId?: string;
    selectedFeatureId?: string;
    paddingSize?: "large" | "small";
    // used when there are no features to fit, defaults to the Netherlands
    fallbackBounds?: MapBounds;
};

export function useMapEditorSelection({
    allLayerData,
    selectedLayerId,
    selectedFeatureId,
    paddingSize,
    fallbackBounds = NETHERLANDS_BOUNDS,
}: UseMapEditorSelectionParams) {
    const selectedLayer = useMemo<MapEditorLayerFragment | undefined>(() => {
        return selectedLayerId ? allLayerData?.find(layer => layer.id === selectedLayerId) : undefined;
    }, [allLayerData, selectedLayerId]);

    const selectedFeature = useMemo<GeoJSON.Feature | undefined>(() => {
        if (allLayerData === undefined) return undefined;

        for (const layer of allLayerData) {
            const match = layer.featureCollection?.features.find(x => x.properties.id === selectedFeatureId);
            if (match !== undefined) return match as GeoJSON.Feature;
        }

        return undefined;
    }, [allLayerData, selectedFeatureId]);

    const mapBounds = useMemo<MapBounds>(() => {
        if (selectedFeature) {
            return { bbox: bbox(selectedFeature), kind: "feature" };
        }

        if (selectedLayer?.featureCollection && selectedLayer.featureCollection.features.length > 0) {
            return { bbox: bbox(selectedLayer.featureCollection as GeoJSON.FeatureCollection), kind: "layer" };
        }

        const allFeatures = (allLayerData ?? []).flatMap(layer => (layer.featureCollection?.features ?? []) as GeoJSON.Feature[]);
        if (allFeatures.length > 0) {
            return { bbox: bbox(featureCollection(allFeatures)), kind: "features" };
        }

        return fallbackBounds;
    }, [selectedFeature, selectedLayer, allLayerData, fallbackBounds]);

    const adjustedMapPadding = useMemo(() => {
        if (selectedFeatureId === undefined && selectedLayerId === undefined) {
            return undefined;
        }

        return paddingSize === "small"
            ? { right: MAP_PADDING_PANEL_HORIZONTAL }
            : { right: MAP_PADDING_PANEL_HORIZONTAL + (MAP_PANEL_WIDTH_WIDE - MAP_PANEL_WIDTH_THIN) };
    }, [paddingSize, selectedFeatureId, selectedLayerId]);

    return {
        selectedLayer,
        selectedFeature,
        mapBounds,
        adjustedMapPadding,
    };
}
